// CubeViewController: manages which 2D face is visible and its orientation.
// Orientation is stored as orthonormal basis vectors chosen from {±X, ±Y, ±Z}.
// Rotations are in 90° steps with exact integer math (no floats).

export class CubeViewController {
  constructor() {
    // Start with front=+Z, up=+Y, right=+X (right-handed basis).
    this.front = [0, 0, 1];
    this.up    = [0, 1, 0];
    this.right = [1, 0, 0];
  }

  // --- Public API (hook your buttons to these) ---

  // Rotate the 2D face (current plane) by 90° CCW/CW around the viewer’s normal.
  rotate2DLeft()  { this.#roll(+1); }
  rotate2DRight() { this.#roll(-1); }

  // Rotate the 3D object to bring a neighboring face into view.
  // Conventions (consistent and easy to reason about):
  // - rotate3DLeft: yaw +90° around up → the RIGHT face becomes the new FRONT.
  // - rotate3DRight: yaw -90° around up → the LEFT face becomes the new FRONT.
  // - rotate3DUp: pitch +90° around right → the TOP face becomes the new FRONT.
  // - rotate3DDown: pitch -90° around right → the BOTTOM face becomes the new FRONT.
  rotate3DLeft()  { this.#yaw(+1); }
  rotate3DRight() { this.#yaw(-1); }
  rotate3DUp()    { this.#pitch(+1); }
  rotate3DDown()  { this.#pitch(-1); }

  // --- Introspection helpers ---

  // Returns a minimal snapshot you can bind to your UI.
  getState() {
    return {
      front: this.#faceName(this.front),
      upOnScreen: this.#axisName(this.up),
      rightOnScreen: this.#axisName(this.right),
      // 2D rotation of the face in degrees, relative to the face’s canonical “upright”.
      // Computed by comparing current (front, up) to the canonical orientation.
      rotation2D: this.#computeFaceRotationDegrees(),
      // A 3x3 integer rotation matrix (columns are right, up, front).
      matrix: [ this.right, this.up, this.front ],
    };
  }

  // Resets to canonical view (front=+Z, up=+Y).
  reset() {
    this.front = [0,0,1];
    this.up    = [0,1,0];
    this.right = [1,0,0];
  }

  // --- Private rotation primitives (90° only) ---

  // Roll around FRONT: (UP, RIGHT) -> (RIGHT, -UP) for +1; reverse for -1
  #roll(dir) {
    if (dir === +1) {
      const newUp = this.right;
      const newRight = this.#neg(this.up);
      this.up = newUp; this.right = newRight;
    } else {
      const newUp = this.#neg(this.right);
      const newRight = this.up;
      this.up = newUp; this.right = newRight;
    }
    // front unchanged
  }

  // Yaw around UP: (FRONT, RIGHT) -> (RIGHT, -FRONT) for +1; reverse for -1
  #yaw(dir) {
    if (dir === +1) {
      const newFront = this.right;
      const newRight = this.#neg(this.front);
      this.front = newFront; this.right = newRight;
    } else {
      const newFront = this.#neg(this.right);
      const newRight = this.front;
      this.front = newFront; this.right = newRight;
    }
    // up unchanged
  }

  // Pitch around RIGHT: (FRONT, UP) -> (UP, -FRONT) for +1; reverse for -1
  #pitch(dir) {
    if (dir === +1) {
      const newFront = this.up;
      const newUp = this.#neg(this.front);
      this.front = newFront; this.up = newUp;
    } else {
      const newFront = this.#neg(this.up);
      const newUp = this.front;
      this.front = newFront; this.up = newUp;
    }
    // right unchanged
  }

  // --- Utilities ---

  #neg(v){ return [-v[0], -v[1], -v[2]]; }

  #axisName(v){
    if (v[0] ===  1) return "+X";
    if (v[0] === -1) return "-X";
    if (v[1] ===  1) return "+Y";
    if (v[1] === -1) return "-Y";
    if (v[2] ===  1) return "+Z";
    if (v[2] === -1) return "-Z";
    return "??";
    }

  #faceName(v){
    // Friendly names for UI labels
    const m = this.#axisName(v);
    switch (m) {
      case "+X": return "right";
      case "-X": return "left";
      case "+Y": return "top";
      case "-Y": return "bottom";
      case "+Z": return "front";
      case "-Z": return "back";
      default: return "unknown";
    }
  }

  // Determine the 2D rotation angle (0/90/180/270) for the current front face,
  // relative to a canonical orientation for that face where "upOnScreen" points:
  //   front=±X → canonical up = +Y, right = ±Z depending on front sign
  //   front=±Y → canonical up = +Z, right = ±X
  //   front=±Z → canonical up = +Y, right = ∓X
  // This picks a consistent, intuitive “upright” for each face.
  #computeFaceRotationDegrees() {
    // Build canonical (up0, right0) for the current front.
    const f = this.#axisName(this.front);
    let up0, right0;
    switch (f) {
      case "+X": up0 = [0,1,0]; right0 = [0,0, 1]; break; // +Z to the right
      case "-X": up0 = [0,1,0]; right0 = [0,0,-1]; break;
      case "+Y": up0 = [0,0,1]; right0 = [1,0, 0]; break;
      case "-Y": up0 = [0,0,1]; right0 = [-1,0,0]; break;
      case "+Z": up0 = [0,1,0]; right0 = [-1,0,0]; break; // +X goes left in screen space
      case "-Z": up0 = [0,1,0]; right0 = [ 1,0,0]; break;
      default:   up0 = [0,1,0]; right0 = [1,0,0];
    }
    // Compare current (up, right) to rotated versions of (up0, right0).
    // Rotations around 'front' by k * 90° map:
    //   k=0: (up0, right0)
    //   k=1: ( right0, -up0)
    //   k=2: (-up0, -right0)
    //   k=3: (-right0, up0)
    const eq = (a,b)=> a[0]===b[0] && a[1]===b[1] && a[2]===b[2];
    const neg = this.#neg.bind(this);

    const up1 = right0,        right1 = neg(up0);
    const up2 = neg(up0),      right2 = neg(right0);
    const up3 = neg(right0),   right3 = up0;

    if (eq(this.up, up0) && eq(this.right, right0)) return 0;
    if (eq(this.up, up1) && eq(this.right, right1)) return 90;
    if (eq(this.up, up2) && eq(this.right, right2)) return 180;
    if (eq(this.up, up3) && eq(this.right, right3)) return 270;

    // Fallback (shouldn't happen with only 90° ops)
    return 0;
  }
}

// -------------------- Example wiring --------------------
// const ctrl = new CubeViewController();
//
// // Hook up buttons:
// left2DBtn.onclick  = () => { ctrl.rotate2DLeft();  render(ctrl.getState()); };
// right2DBtn.onclick = () => { ctrl.rotate2DRight(); render(ctrl.getState()); };
//
// left3DBtn.onclick  = () => { ctrl.rotate3DLeft();  render(ctrl.getState()); };
// right3DBtn.onclick = () => { ctrl.rotate3DRight(); render(ctrl.getState()); };
// up3DBtn.onclick    = () => { ctrl.rotate3DUp();    render(ctrl.getState()); };
// down3DBtn.onclick  = () => { ctrl.rotate3DDown();  render(ctrl.getState()); };
//
// function render(state){
//   // state.front: "front" | "back" | "left" | "right" | "top" | "bottom"
//   // state.rotation2D: 0|90|180|270
//   // state.matrix: [[rx,ry,rz],[ux,uy,uz],[fx,fy,fz]]
//   // Use this to update your canvas/SVG/WebGL/DOM.
//   console.log(state);
// }

